import { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_BASE } from '../../../../ApiUrl';
import {
  CONSENT_MAP,
  createEmptyMembership,
  createEmptyPublication,
  createEmptyAward,
  createEmptyReference,
} from '../constants';

const CREDENTIAL_URL = `${API_BASE}/combined-credential-record/`;
const CONTACT_URL = `${API_BASE}/contact-records/`;
const DELETE_CREDENTIAL_URL = `${API_BASE}/credential-records/`;

export const useReferenceRecords = (professionalProfileId, tenantId) => {
  const [membershipRecords, setMembershipRecords] = useState([createEmptyMembership()]);
  const [publicationRecords, setPublicationRecords] = useState([createEmptyPublication()]);
  const [awardRecords, setAwardRecords] = useState([createEmptyAward()]);
  const [referenceRecords, setReferenceRecords] = useState([createEmptyReference()]);

  // ----- Hydration helpers -----
  const MEMBERSHIP_STATUS_MAP_REVERSE = {
    ACTIVE: 'Active',
    EXPIRED: 'Lapsed',
    ARCHIVED: 'Honorary',
    DRAFT: 'Pending',
    REVOKED: 'Lapsed',
  };

  const hydrateMembershipsPublicationsAwards = (records) => {
    if (!Array.isArray(records)) return;
    const byType = {};
    records.forEach((rec) => {
      if (!byType[rec.record_type]) byType[rec.record_type] = [];
      byType[rec.record_type].push(rec);
    });

    // Memberships
    const membershipApi = byType.MEMBERSHIP || [];
    if (membershipApi.length > 0) {
      setMembershipRecords(
        membershipApi.map((rec) => ({
          id: `membership-${rec.id}`,
          _backendId: rec.id,
          body_institution: rec.title || '',
          grade: rec.level_or_grade || '',
          membership_number: rec.credential_number || '',
          start_date: rec.start_date || '',
          status: MEMBERSHIP_STATUS_MAP_REVERSE[rec.status] || '',
          errors: {},
        }))
      );
    }

    // Publications
    const pubApi = byType.PUBLICATION || [];
    if (pubApi.length > 0) {
      setPublicationRecords(
        pubApi.map((rec) => ({
          id: `publication-${rec.id}`,
          _backendId: rec.id,
          title: rec.title || '',
          publisher: rec.issuing_body_snapshot || '',
          date: rec.issue_date || '',
          url: rec.publication_url || '',
          errors: {},
        }))
      );
    }

    // Awards
    const awardApi = byType.AWARD || [];
    if (awardApi.length > 0) {
      setAwardRecords(
        awardApi.map((rec) => ({
          id: `award-${rec.id}`,
          _backendId: rec.id,
          award_name: rec.title || '',
          issuer: rec.issuing_body_snapshot || '',
          date: rec.issue_date || '',
          reason_context: rec.restrictions_or_limitations || '',
          errors: {},
        }))
      );
    }
  };

  const hydrateReferencesFromContacts = (records) => {
    if (!Array.isArray(records)) return;
    const refs = records.filter((rec) => rec.contact_type === 'PROFESSIONAL_REFERENCE');
    if (refs.length === 0) return;

    const CONSENT_MAP_REVERSE = { true: 'Yes', false: 'No' };
    setReferenceRecords(
      refs.map((rec) => ({
        id: `ref-${rec.id}`,
        _backendId: rec.id,
        name: rec.full_name || '',
        job_title: rec.job_title || '',
        organisation: rec.organization_name_snapshot || '',
        relationship: rec.relationship || '',
        email: rec.email || '',
        phone: rec.phone || '',
        consent_to_share: CONSENT_MAP_REVERSE[String(rec.consent_given)] || '',
        errors: {},
      }))
    );
  };

  // Hydrate from related records
  const hydrateFromRelatedRecords = (data) => {
    if (!data) return;
    
    // Hydrate memberships, publications, awards from credentials
    if (data.credentials && Array.isArray(data.credentials)) {
      hydrateMembershipsPublicationsAwards(data.credentials);
    }
    
    // Hydrate references from contacts
    if (data.references && Array.isArray(data.references)) {
      hydrateReferencesFromContacts(data.references);
    }
    
    // Also check for contacts if references are in contacts field
    if (data.contacts && Array.isArray(data.contacts)) {
      hydrateReferencesFromContacts(data.contacts);
    }
  };

  // Fetch from related-records endpoint only
  useEffect(() => {
    if (!professionalProfileId) return;

    const loadReferences = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/professional-profiles/${professionalProfileId}/related-records/`
        );
        const data = res?.data?.data || res?.data;
        
        // Hydrate all reference data
        hydrateFromRelatedRecords(data);
      } catch (err) {
        console.error('Failed to load references from related-records API', err);
        // Keep the default empty states
      }
    };

    loadReferences();
  }, [professionalProfileId]);

  // ----- Extract backend IDs -----
  const extractMembershipBackendId = (recordId) => {
    const match = /^membership-(\d+)$/.exec(String(recordId));
    return match ? match[1] : null;
  };
  const extractPublicationBackendId = (recordId) => {
    const match = /^publication-(\d+)$/.exec(String(recordId));
    return match ? match[1] : null;
  };
  const extractAwardBackendId = (recordId) => {
    const match = /^award-(\d+)$/.exec(String(recordId));
    return match ? match[1] : null;
  };
  const extractReferenceBackendId = (recordId) => {
    const match = /^ref-(\d+)$/.exec(String(recordId));
    return match ? match[1] : null;
  };

  // ----- Build payloads -----
  const MEMBERSHIP_STATUS_MAP = {
    Active: 'ACTIVE',
    Lapsed: 'EXPIRED',
    Honorary: 'ARCHIVED',
    Pending: 'DRAFT',
  };

  const buildMembershipsPublicationsAwardsPayload = () => {
    const records = [];
    // Memberships
    membershipRecords
      .filter((rec) => rec.body_institution || rec.title || rec.award_name)
      .forEach((rec) => {
        records.push({
          _backendId: extractMembershipBackendId(rec.id),
          professional: professionalProfileId,
          tenant: tenantId,
          record_type: 'MEMBERSHIP',
          title: rec.body_institution || '',
          level_or_grade: rec.grade || '',
          issuing_body_snapshot: rec.body_institution || '',
          credential_number: rec.membership_number || '',
          start_date: rec.start_date || null,
          status: MEMBERSHIP_STATUS_MAP[rec.status] || 'DRAFT',
        });
      });

    // Publications
    publicationRecords
      .filter((rec) => rec.title || rec.publisher)
      .forEach((rec) => {
        records.push({
          _backendId: extractPublicationBackendId(rec.id),
          professional: professionalProfileId,
          tenant: tenantId,
          record_type: 'PUBLICATION',
          title: rec.title || '',
          issuing_body_snapshot: rec.publisher || '',
          issue_date: rec.date || null,
          publication_url: rec.url || '',
          status: 'ACTIVE',
        });
      });

    // Awards
    awardRecords
      .filter((rec) => rec.award_name || rec.issuer)
      .forEach((rec) => {
        records.push({
          _backendId: extractAwardBackendId(rec.id),
          professional: professionalProfileId,
          tenant: tenantId,
          record_type: 'AWARD',
          title: rec.award_name || '',
          issuing_body_snapshot: rec.issuer || '',
          issue_date: rec.date || null,
          restrictions_or_limitations: rec.reason_context || '',
          status: 'ACTIVE',
        });
      });

    return records;
  };

  const buildReferencesContactPayload = () => {
    return referenceRecords
      .filter((rec) => rec.name || rec.organisation)
      .map((rec) => ({
        _backendId: extractReferenceBackendId(rec.id),
        professional: professionalProfileId,
        tenant: tenantId,
        contact_type: 'PROFESSIONAL_REFERENCE',
        full_name: rec.name || '',
        job_title: rec.job_title || '',
        organization_name_snapshot: rec.organisation || '',
        relationship: rec.relationship || '',
        email: rec.email || '',
        phone: toE164(rec.phone),
        consent_given: CONSENT_MAP[rec.consent_to_share] ?? false,
        consent_given_at: CONSENT_MAP[rec.consent_to_share]
          ? new Date().toISOString()
          : null,
        verification_status: 'SELF_DECLARED',
      }));
  };

  // Helper: convert phone to E.164
  const toE164 = (rawPhone) => {
    if (!rawPhone) return '';
    const trimmed = rawPhone.trim();
    const hasPlus = trimmed.startsWith('+');
    const digitsOnly = trimmed.replace(/[^\d]/g, '');
    if (!digitsOnly) return '';
    if (hasPlus) return `+${digitsOnly}`;
    return `+91${digitsOnly}`; // Default to India, change if needed
  };

  // ----- Submit functions -----
  const submitMembershipsPublicationsAwards = async () => {
    if (!professionalProfileId) {
      throw new Error('Professional profile ID missing.');
    }
    const records = buildMembershipsPublicationsAwardsPayload();
    const toCreate = records.filter((rec) => !rec._backendId);
    const toUpdate = records.filter((rec) => rec._backendId);

    if (toCreate.length > 0) {
      await axios.post(CREDENTIAL_URL, toCreate.map(({ _backendId, ...rest }) => rest));
    }
    if (toUpdate.length > 0) {
      await Promise.all(
        toUpdate.map(({ _backendId, ...rest }) =>
          axios.put(`${DELETE_CREDENTIAL_URL}${_backendId}/`, rest)
        )
      );
    }
  };

  const submitReferences = async () => {
    if (!professionalProfileId) {
      throw new Error('Professional profile ID missing.');
    }
    const records = buildReferencesContactPayload();
    const toCreate = records.filter((rec) => !rec._backendId);
    const toUpdate = records.filter((rec) => rec._backendId);

    if (toCreate.length > 0) {
      await axios.post(CONTACT_URL, toCreate.map(({ _backendId, ...rest }) => rest));
    }
    if (toUpdate.length > 0) {
      await Promise.all(
        toUpdate.map(({ _backendId, ...rest }) =>
          axios.put(`${CONTACT_URL}${_backendId}/`, rest)
        )
      );
    }
  };

  // ----- Delete handlers -----
  const removeMembershipRecord = (index) => {
    const record = membershipRecords[index];
    const backendId = record ? extractMembershipBackendId(record.id) : null;
    setMembershipRecords((prev) => {
      if (prev.length === 1) return [createEmptyMembership()];
      return prev.filter((_, i) => i !== index);
    });
    if (backendId) {
      axios.delete(`${DELETE_CREDENTIAL_URL}${backendId}/`).catch((err) => {
        console.error('Failed to delete membership record', err);
        Swal.fire({ icon: 'error', title: 'Delete Failed', text: 'Could not delete from server.' });
      });
    }
  };

  const removePublicationRecord = (index) => {
    const record = publicationRecords[index];
    const backendId = record ? extractPublicationBackendId(record.id) : null;
    setPublicationRecords((prev) => {
      if (prev.length === 1) return [createEmptyPublication()];
      return prev.filter((_, i) => i !== index);
    });
    if (backendId) {
      axios.delete(`${DELETE_CREDENTIAL_URL}${backendId}/`).catch((err) => {
        console.error('Failed to delete publication record', err);
        Swal.fire({ icon: 'error', title: 'Delete Failed', text: 'Could not delete from server.' });
      });
    }
  };

  const removeAwardRecord = (index) => {
    const record = awardRecords[index];
    const backendId = record ? extractAwardBackendId(record.id) : null;
    setAwardRecords((prev) => {
      if (prev.length === 1) return [createEmptyAward()];
      return prev.filter((_, i) => i !== index);
    });
    if (backendId) {
      axios.delete(`${DELETE_CREDENTIAL_URL}${backendId}/`).catch((err) => {
        console.error('Failed to delete award record', err);
        Swal.fire({ icon: 'error', title: 'Delete Failed', text: 'Could not delete from server.' });
      });
    }
  };

  const removeReferenceRecord = (index) => {
    const record = referenceRecords[index];
    const backendId = record ? extractReferenceBackendId(record.id) : null;
    setReferenceRecords((prev) => {
      if (prev.length === 1) return [createEmptyReference()];
      return prev.filter((_, i) => i !== index);
    });
    if (backendId) {
      axios.delete(`${CONTACT_URL}${backendId}/`).catch((err) => {
        console.error('Failed to delete reference record', err);
        Swal.fire({ icon: 'error', title: 'Delete Failed', text: 'Could not delete from server.' });
      });
    }
  };

  // ----- Record management helpers (add/remove/change) -----
  const addRecord = (setter, creator) => {
    setter((prev) => [...prev, creator()]);
  };

  const removeRecord = (setter, index, creator) => {
    setter((prev) => {
      if (prev.length === 1) return [creator()];
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleRecordChange = (setter, index, field, value) => {
    setter((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  return {
    // State
    membershipRecords,
    publicationRecords,
    awardRecords,
    referenceRecords,
    // Setters
    setMembershipRecords,
    setPublicationRecords,
    setAwardRecords,
    setReferenceRecords,
    // Hydration
    hydrateMembershipsPublicationsAwards,
    hydrateReferencesFromContacts,
    hydrateFromRelatedRecords,
    // Validation (always true)
    validate: () => ({}),
    // Submits
    submitMembershipsPublicationsAwards,
    submitReferences,
    // Delete handlers
    removeMembershipRecord,
    removePublicationRecord,
    removeAwardRecord,
    removeReferenceRecord,
    // Record helpers
    addRecord,
    removeRecord,
    handleRecordChange,
  };
};